import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { DiagnosticBanner } from "@/components/app/diagnostic-banner";
import { DevErrorOverlay } from "@/components/app/dev-error-overlay";
import { ClientErrorReporter } from "@/components/app/client-error-reporter";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "InboxPulse — Email Deliverability Monitoring" },
      {
        name: "description",
        content:
          "Enterprise email deliverability and campaign analytics. Monitor inbox placement, spam risk, reputation, and provider events in real-time.",
      },
      { name: "author", content: "InboxPulse" },
      { property: "og:title", content: "InboxPulse — Email Deliverability Monitoring" },
      {
        property: "og:description",
        content:
          "Enterprise email deliverability and campaign analytics. Monitor inbox placement, spam risk, reputation, and provider events in real-time.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "InboxPulse — Email Deliverability Monitoring" },
      {
        name: "twitter:description",
        content:
          "Enterprise email deliverability and campaign analytics. Monitor inbox placement, spam risk, reputation, and provider events in real-time.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/SrylqTxWPCRr9RHbrAvH1c05HnA2/social-images/social-1778940004419-ruffy_logo.webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/SrylqTxWPCRr9RHbrAvH1c05HnA2/social-images/social-1778940004419-ruffy_logo.webp",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style>{assetWatchdogStyles}</style>
      </head>
      <body>
        <noscript>
          <div id="asset-watchdog" role="alert">
            <div className="asset-watchdog-card">
              <strong>⚠️ JavaScript ist blockiert</strong>
              <p>
                Diese Seite braucht JavaScript und Stylesheets. Deaktiviere Brave Shields, AdBlocker
                oder Übersetzung für diesen Tab und lade die Seite neu.
              </p>
            </div>
          </div>
        </noscript>
        {children}
        <div
          id="asset-watchdog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="asset-watchdog-title"
          hidden
        >
          <div className="asset-watchdog-card">
            <strong id="asset-watchdog-title">⚠️ Seite wird ohne Design geladen</strong>
            <p id="asset-watchdog-summary">
              Das CSS- oder JavaScript-Bundle wurde nicht korrekt geladen. Häufig blockieren
              Schutzfunktionen, Übersetzung oder Erweiterungen die Assets.
            </p>
            <div
              id="asset-watchdog-diagnosis"
              style={{
                display: "none",
                margin: "0 0 14px",
                padding: "10px 12px",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              <span id="asset-watchdog-diagnosis-text"></span>
            </div>
            <ol id="asset-watchdog-steps">
              <li>
                Im gleichen Tab auf das Schutzschild-/AdBlock-Icon klicken und für diese Seite
                deaktivieren.
              </li>
              <li>Beim Übersetzungs-Popup "Original anzeigen" wählen.</li>
              <li>Mit Strg+Shift+R bzw. Cmd+Shift+R hart neu laden.</li>
              <li>
                Wenn es bleibt: Inkognito oder einen anderen Browser ohne Erweiterungen testen.
              </li>
            </ol>
            <details
              id="asset-watchdog-debug"
              style={{
                display: "none",
                margin: "4px 0 14px",
                padding: "10px 12px",
                background: "#0f172a",
                border: "1px solid #fb923c",
                borderRadius: "6px",
                color: "#e2e8f0",
                fontSize: "12px",
              }}
            >
              <summary
                style={{ cursor: "pointer", fontWeight: 600, color: "#fb923c", listStyle: "none" }}
              >
                Debug-Panel · <span id="asset-watchdog-count">0</span> blockierte Asset(s)
              </summary>
              <div
                id="asset-watchdog-net"
                style={{
                  marginTop: "8px",
                  fontFamily: "ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",
                  fontSize: "11px",
                  color: "#cbd5e1",
                }}
              ></div>
              <table
                id="asset-watchdog-table"
                style={{
                  width: "100%",
                  marginTop: "8px",
                  borderCollapse: "collapse",
                  fontFamily: "ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",
                  fontSize: "11px",
                  color: "#e2e8f0",
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      color: "#94a3b8",
                      borderBottom: "1px solid #334155",
                    }}
                  >
                    <th style={{ padding: "4px 6px", width: "60px" }}>Typ</th>
                    <th style={{ padding: "4px 6px", width: "110px" }}>Zeit</th>
                    <th style={{ padding: "4px 6px" }}>URL</th>
                    <th style={{ padding: "4px 6px", width: "110px" }}>Netz</th>
                    <th style={{ padding: "4px 6px", width: "200px" }}>Möglicher Grund</th>
                  </tr>
                </thead>
                <tbody id="asset-watchdog-tbody"></tbody>
              </table>
            </details>
            <button type="button" id="asset-watchdog-reload">
              Seite neu laden
            </button>
            <button type="button" id="asset-watchdog-copy">
              Debug-Info kopieren
            </button>
            <button type="button" id="asset-watchdog-close">
              Trotzdem fortfahren
            </button>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{ __html: assetWatchdogScript }} />
        <Scripts />
      </body>
    </html>
  );
}

const assetWatchdogStyles = `
  #asset-watchdog[hidden]{display:none!important}
  #asset-watchdog{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,.88);font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a}
  #asset-watchdog .asset-watchdog-card{max-width:620px;width:100%;box-sizing:border-box;background:#fff;border:1px solid #fb923c;border-radius:12px;padding:24px;box-shadow:0 24px 80px rgba(0,0,0,.4);font-size:14px;line-height:1.5}
  #asset-watchdog strong{display:block;font-size:19px;margin-bottom:8px;color:#0f172a}
  #asset-watchdog p{margin:0 0 14px;color:#475569}
  #asset-watchdog ol{margin:0 0 16px;padding-left:22px;color:#334155}
  #asset-watchdog li{margin-bottom:6px}
  #asset-watchdog button{margin:0 8px 0 0;padding:8px 14px;border-radius:6px;border:1px solid #cbd5e1;background:#fff;color:#0f172a;cursor:pointer;font-weight:600}
  #asset-watchdog button:first-of-type{background:#0f172a;color:#fff;border-color:#0f172a}
`;

const assetWatchdogScript = `
  (function(){
    var failed = [];
    var probeResult = null; // 'online' | 'offline' | 'blocked' | 'pending'
    var cspViolations = []; // accumulated CSP violation directives
    var currentDiagnosis = 'unknown';
    var currentDiagnosisText = '';
    var TELEMETRY_URL = '/api/public/watchdog';
    var telemetrySent = {}; // dedupe by diagnosis+assetUrl
    function sendTelemetry(payload){
      try {
        var key = payload.diagnosis + '|' + (payload.assetUrl || '');
        if (telemetrySent[key]) return;
        telemetrySent[key] = true;
        var body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
          var blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon(TELEMETRY_URL, blob)) return;
        }
        fetch(TELEMETRY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          keepalive: true,
          credentials: 'omit',
          mode: 'cors'
        }).catch(function(){ /* swallow */ });
      } catch(_) {}
    }
    function reportDiagnosis(kind, assetType, assetUrl, extra){
      sendTelemetry({
        diagnosis: kind,
        assetType: assetType || null,
        assetUrl: assetUrl || null,
        navigatorOnline: !!navigator.onLine,
        probeResult: probeResult,
        pageUrl: window.location.href,
        referrer: document.referrer || null,
        userAgent: (navigator.userAgent || '').slice(0, 1024),
        extra: extra || {}
      });
    }
    function pad(n){return n<10?'0'+n:''+n;}
    function ts(d){
      return pad(d.getHours())+':'+pad(d.getMinutes())+':'+pad(d.getSeconds())+'.'+String(d.getMilliseconds()).padStart(3,'0');
    }
    function typeOf(el){
      if (el.tagName === 'LINK') {
        var rel = (el.rel || el.getAttribute('rel') || '').toLowerCase();
        if (rel.indexOf('stylesheet') !== -1) return 'CSS';
        if (rel.indexOf('modulepreload') !== -1) return 'JS·preload';
        return 'LINK';
      }
      if (el.tagName === 'SCRIPT') {
        var type = (el.type || '').toLowerCase();
        return type === 'module' ? 'JS·module' : 'JS';
      }
      return el.tagName;
    }
    function show(){var el=document.getElementById('asset-watchdog'); if(el) el.hidden=false;}
    function close(){var el=document.getElementById('asset-watchdog'); if(el) el.hidden=true;}
    function urlOf(el){
      try {
        if (el.tagName === 'LINK') return el.href || el.getAttribute('href') || '';
        if (el.tagName === 'SCRIPT') return el.src || el.getAttribute('src') || '';
      } catch(_) {}
      return '';
    }
    function isAppAsset(url){
      if (!url) return false;
      try {
        var u = new URL(url, window.location.href);
        if (u.origin !== window.location.origin) return false;
        // ignore browser-extension / devtools injected assets
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
        return /\\.(css|js|mjs)(\\?|$)/i.test(u.pathname) || /\\/(assets|_build|_app)\\//.test(u.pathname);
      } catch(_) { return false; }
    }
    function escapeHtml(s){
      return String(s).replace(/[&<>"']/g,function(c){
        return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c];
      });
    }
    // Heuristik: gibt einen kurzen, menschenlesbaren Hinweis zurück, warum
    // ein Asset wahrscheinlich blockiert wurde — abhängig von Typ + URL-Muster.
    function reasonHint(type, url){
      var t = String(type || '');
      var u = String(url || '').toLowerCase();
      if (t.indexOf('CSP') === 0) {
        return 'Von Content-Security-Policy blockiert — Direktive in CSP-Header erlauben.';
      }
      if (/google-analytics|googletagmanager|gtag|gtm\\.js|doubleclick|facebook\\.net|hotjar|segment|mixpanel|fullstory|clarity\\.ms/.test(u)) {
        return 'Tracking-Skript — wird typischerweise von AdBlockern (uBlock, Brave Shields, Privacy Badger) blockiert.';
      }
      if (/fonts\\.googleapis|fonts\\.gstatic|use\\.typekit|use\\.fontawesome/.test(u)) {
        return 'Externe Font — von Privacy-Erweiterung oder Firmen-Proxy blockiert.';
      }
      if (/cdn|unpkg|jsdelivr|cloudflare|cloudfront/.test(u)) {
        return 'CDN-Asset — DNS-Filter, Firewall oder Content-Blocker (NoScript) blockiert externe CDNs.';
      }
      if (t.indexOf('CSS') === 0 || /\\.css(\\?|$)/.test(u)) {
        return 'Stylesheet — meist von AdBlocker-Cosmetic-Filter oder Übersetzungs-Tool entfernt.';
      }
      if (t.indexOf('JS') === 0 || /\\.m?js(\\?|$)/.test(u)) {
        return 'Skript-Bundle — wahrscheinlich von Skript-Blocker (NoScript), Brave Shields oder Firewall blockiert.';
      }
      return 'Unbekannter Asset-Typ — Browser-Erweiterungen prüfen und hart neu laden.';
    }
    function setDiagnosis(kind, text){
      var box = document.getElementById('asset-watchdog-diagnosis');
      var txt = document.getElementById('asset-watchdog-diagnosis-text');
      if (!box || !txt) return;
      var palette = {
        offline:  { bg:'#fef2f2', border:'#fecaca', color:'#991b1b' },
        network:  { bg:'#fff7ed', border:'#fed7aa', color:'#9a3412' },
        blocked:  { bg:'#fefce8', border:'#fde68a', color:'#854d0e' },
        csp:      { bg:'#faf5ff', border:'#e9d5ff', color:'#6b21a8' },
        unknown:  { bg:'#f1f5f9', border:'#cbd5e1', color:'#0f172a' }
      }[kind] || { bg:'#f1f5f9', border:'#cbd5e1', color:'#0f172a' };
      box.style.display = 'block';
      box.style.background = palette.bg;
      box.style.border = '1px solid ' + palette.border;
      box.style.color = palette.color;
      txt.textContent = text;
      currentDiagnosis = kind;
      currentDiagnosisText = text;
      renderSteps(kind);
      var summary = document.getElementById('asset-watchdog-summary');
      if (summary) summary.textContent = SUMMARY[kind] || SUMMARY.unknown;
    }
    var SUMMARY = {
      offline:  'Dein Gerät ist offline. Sobald die Verbindung wieder steht, kannst du die Seite neu laden.',
      network:  'Der Server ist gerade nicht erreichbar. Das ist kein Browser-Block, sondern ein Netzwerkproblem.',
      blocked:  'Der Server antwortet, aber CSS/JS wurden clientseitig blockiert — typischerweise von AdBlocker, Brave Shields oder einer Browser-Erweiterung.',
      csp:      'Eine Content-Security-Policy (CSP) des Servers hat ein Asset verboten. Das ist kein Client-Blocker, sondern eine Server-Konfiguration.',
      unknown:  'Das CSS- oder JavaScript-Bundle wurde nicht korrekt geladen. Wir prüfen gerade, ob es am Netz oder an einer Blockierung liegt.'
    };
    var STEPS = {
      offline: [
        'WLAN, Mobilfunk oder LAN prüfen — bist du wirklich verbunden?',
        'VPN oder Firewall kurz deaktivieren und erneut versuchen.',
        'Sobald wieder online: "Seite neu laden" klicken.'
      ],
      network: [
        'Andere Website öffnen — funktioniert das Internet generell?',
        'VPN/Proxy aus- oder umschalten, Router neustarten.',
        'DNS prüfen (z. B. 1.1.1.1 oder 8.8.8.8) und erneut laden.',
        'Wenn andere Seiten gehen, in 1–2 Minuten erneut versuchen — der Server kann kurz nicht erreichbar sein.'
      ],
      blocked: [
        'Auf das Schild-/AdBlock-Icon in der Adressleiste klicken und für diese Seite deaktivieren (Brave Shields, uBlock, AdGuard …).',
        'Browser-Erweiterungen prüfen — besonders Privacy-, Tracking- und Skript-Blocker (NoScript, Ghostery, Privacy Badger).',
        'Übersetzungs-Popup auf "Original anzeigen" stellen.',
        'Mit Strg+Shift+R bzw. Cmd+Shift+R hart neu laden.',
        'Im Inkognito-Modus / anderen Browser testen — funktioniert es dort, liegt es an einer Erweiterung.'
      ],
      csp: [
        'Dies ist eine Server-seitige Sperre — Browser-Erweiterungen abzuschalten hilft NICHT.',
        'Im Debug-Panel die genaue CSP-Direktive ablesen (z. B. script-src, style-src, connect-src).',
        'Betreiber/Admin: betroffene Quelle in den Content-Security-Policy-Header aufnehmen.',
        'In den DevTools nach "Refused to load … Content Security Policy" suchen — dort steht die exakte Direktive.',
        'Nach Anpassung des CSP-Headers hart neu laden (Strg+Shift+R / Cmd+Shift+R).'
      ],
      unknown: [
        'Mit Strg+Shift+R bzw. Cmd+Shift+R hart neu laden.',
        'Falls vorhanden: Schutzschild-/AdBlock-Icon für diese Seite deaktivieren.',
        'Im Inkognito-Modus testen, um Erweiterungen auszuschließen.'
      ]
    };
    function renderSteps(kind){
      var ol = document.getElementById('asset-watchdog-steps');
      if (!ol) return;
      var items = STEPS[kind] || STEPS.unknown;
      ol.innerHTML = '';
      for (var i=0;i<items.length;i++) {
        var li = document.createElement('li');
        li.textContent = items[i];
        ol.appendChild(li);
      }
    }
    function updateNetPanel(extra){
      var net = document.getElementById('asset-watchdog-net');
      if (!net) return;
      var lines = [
        'navigator.onLine: ' + (navigator.onLine ? 'true' : 'false'),
        'Netzwerk-Probe: ' + (probeResult || 'pending')
      ];
      if (extra) lines.push(extra);
      net.textContent = lines.join('  ·  ');
    }
    function classify(){
      // 1) Browser explicitly says offline → it's a network issue.
      if (!navigator.onLine) {
        setDiagnosis('offline', '🔌 Offline erkannt — bitte Internetverbindung prüfen, dann neu laden.');
        reportDiagnosis('offline', null, failed.length ? failed[0].url : null);
        return;
      }
      // 2) Reachability probe failed → DNS / proxy / network issue.
      if (probeResult === 'offline') {
        setDiagnosis('network', '📡 Server nicht erreichbar — vermutlich ein Netzwerk-/DNS-Problem, keine Blockierung.');
        reportDiagnosis('network', null, failed.length ? failed[0].url : null);
        return;
      }
      // 3a) CSP violation observed → server-side policy block, NOT a client extension.
      if (cspViolations.length > 0) {
        var directives = cspViolations.map(function(v){ return v.directive; })
          .filter(function(d,i,arr){ return arr.indexOf(d) === i; })
          .join(', ');
        setDiagnosis('csp', '🔒 Content-Security-Policy hat Assets blockiert (' + (directives || 'Direktive') + ') — Server-Konfiguration, nicht dein Browser.');
        reportDiagnosis('csp', failed[0] ? failed[0].type : null, failed[0] ? failed[0].url : null, { directives: directives });
        return;
      }
      // 3b) Origin is reachable, no CSP event → clientseitiger Block (Shield, AdBlock, extension).
      if (probeResult === 'online') {
        setDiagnosis('blocked', '🛡️ Server erreichbar, aber Assets wurden clientseitig blockiert — vermutlich Brave Shields, AdBlocker oder eine Browser-Erweiterung.');
        reportDiagnosis('blocked', failed[0] ? failed[0].type : null, failed[0] ? failed[0].url : null);
        return;
      }
      // 4) Probe still running → keep generic copy.
      setDiagnosis('unknown', '🔍 Diagnose läuft …');
    }
    function runProbe(){
      probeResult = 'pending';
      updateNetPanel();
      var url = window.location.origin + '/favicon.ico?ts=' + Date.now();
      var done = false;
      var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timer = setTimeout(function(){
        if (done) return;
        done = true;
        if (ctrl) try { ctrl.abort(); } catch(_) {}
        probeResult = 'offline';
        updateNetPanel('probe: timeout');
        classify();
      }, 4000);
      try {
        fetch(url, { method:'GET', cache:'no-store', mode:'no-cors', signal: ctrl ? ctrl.signal : undefined })
          .then(function(){
            if (done) return; done = true; clearTimeout(timer);
            probeResult = 'online';
            updateNetPanel('probe: ok');
            classify();
          })
          .catch(function(err){
            if (done) return; done = true; clearTimeout(timer);
            probeResult = 'offline';
            updateNetPanel('probe: ' + (err && err.message ? err.message : 'failed'));
            classify();
          });
      } catch(err){
        done = true; clearTimeout(timer);
        probeResult = 'offline';
        updateNetPanel('probe: throw');
        classify();
      }
    }
    function record(entry){
      for (var i=0;i<failed.length;i++) if (failed[i].url===entry.url) return;
      entry.reason = reasonHint(entry.type, entry.url);
      // Snapshot des Netz-/Probe-Status zum Zeitpunkt des Fehlers — bleibt
      // erhalten, auch wenn sich der Status später ändert.
      entry.netOnline = !!navigator.onLine;
      entry.probe = probeResult || 'pending';
      entry.netLabel = (entry.netOnline ? 'online' : 'offline') + ' · probe:' + entry.probe;
      failed.push(entry);
      try { console.error('[asset-watchdog]', entry.type, entry.timeISO, entry.url); } catch(_) {}
      // Forward every individual failed asset URL to the analytics endpoint
      // (deduped by URL via sendTelemetry key). This gives us a complete list
      // of blocked resources per session, independent of the aggregated
      // diagnosis event below.
      reportDiagnosis('asset_failed', entry.type, entry.url, {
        occurredAt: entry.timeISO,
        reason: entry.reason,
        netOnline: entry.netOnline,
        probe: entry.probe
      });
      var panel = document.getElementById('asset-watchdog-debug');
      var tbody = document.getElementById('asset-watchdog-tbody');
      var count = document.getElementById('asset-watchdog-count');
      if (panel && tbody) {
        panel.style.display = 'block';
        panel.open = true;
        var row = document.createElement('tr');
        row.style.borderBottom = '1px solid #1e293b';
        var netColor = entry.netOnline ? (entry.probe === 'online' ? '#86efac' : (entry.probe === 'offline' ? '#fca5a5' : '#fcd34d')) : '#fca5a5';
        row.innerHTML =
          '<td style="padding:4px 6px;color:#fb923c;white-space:nowrap;vertical-align:top">' + escapeHtml(entry.type) + '</td>' +
          '<td style="padding:4px 6px;color:#94a3b8;white-space:nowrap;vertical-align:top" title="' + escapeHtml(entry.timeISO) + '">' + escapeHtml(entry.timeLocal) + '</td>' +
          '<td style="padding:4px 6px;word-break:break-all;vertical-align:top"><a href="' + escapeHtml(entry.url) + '" target="_blank" rel="noopener noreferrer" style="color:#e2e8f0;text-decoration:underline">' + escapeHtml(entry.url) + '</a></td>' +
          '<td style="padding:4px 6px;color:' + netColor + ';white-space:nowrap;vertical-align:top;font-size:10px">' + escapeHtml(entry.netLabel) + '</td>' +
          '<td style="padding:4px 6px;color:#fde68a;vertical-align:top;line-height:1.4">' + escapeHtml(entry.reason) + '</td>';
        tbody.appendChild(row);
      }
      if (count) count.textContent = String(failed.length);
      updateNetPanel();
    }
    document.addEventListener('click',function(e){
      if(e.target && e.target.id==='asset-watchdog-reload') window.location.reload();
      if(e.target && e.target.id==='asset-watchdog-close') close();
      if(e.target && e.target.id==='asset-watchdog-copy') copyDebugInfo(e.target);
    });
    function copyDebugInfo(btn){
      var lines = [];
      lines.push('=== Asset-Watchdog Debug-Info ===');
      lines.push('Zeitpunkt: ' + new Date().toISOString());
      lines.push('Seite: ' + window.location.href);
      lines.push('User-Agent: ' + (navigator.userAgent || 'n/a'));
      lines.push('navigator.onLine: ' + (navigator.onLine ? 'true' : 'false'));
      lines.push('Netzwerk-Probe: ' + (probeResult || 'pending'));
      lines.push('Diagnose: ' + currentDiagnosis + (currentDiagnosisText ? ' — ' + currentDiagnosisText : ''));
      if (cspViolations.length) {
        var ds = cspViolations.map(function(v){ return v.directive + (v.url ? ' → ' + v.url : ''); });
        lines.push('CSP-Verletzungen: ' + ds.join('; '));
      }
      lines.push('Blockierte Assets (' + failed.length + '):');
      if (failed.length === 0) {
        lines.push('  (keine erfasst)');
      } else {
        for (var i=0;i<failed.length;i++) {
          var f = failed[i];
          lines.push('  - [' + f.type + '] ' + f.timeISO + ' · ' + (f.netLabel || '') );
          lines.push('      URL: ' + f.url);
          if (f.reason) lines.push('      Grund: ' + f.reason);
        }
      }
      var text = lines.join('\\n');
      var orig = btn && btn.textContent;
      function done(ok){
        if (!btn) return;
        btn.textContent = ok ? '✓ Kopiert' : '⚠ Kopieren fehlgeschlagen';
        setTimeout(function(){ if (btn && orig != null) btn.textContent = orig; }, 1800);
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function(){ done(true); }, function(){ fallback(); });
          return;
        }
      } catch(_) {}
      fallback();
      function fallback(){
        try {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly','');
          ta.style.position='fixed'; ta.style.left='-9999px';
          document.body.appendChild(ta);
          ta.select();
          var ok = document.execCommand && document.execCommand('copy');
          document.body.removeChild(ta);
          done(!!ok);
        } catch(_) { done(false); }
      }
    }
    // React to live online/offline transitions while overlay is visible.
    window.addEventListener('online', function(){ updateNetPanel(); classify(); });
    window.addEventListener('offline', function(){ probeResult = 'offline'; updateNetPanel(); classify(); });
    // CSP violations are dispatched as 'securitypolicyviolation' events — these
    // are *always* a real block, never a network problem.
    document.addEventListener('securitypolicyviolation', function(ev){
      var url = ev.blockedURI || '(inline)';
      var now = new Date();
      var directive = ev.violatedDirective || 'block';
      cspViolations.push({ directive: directive, effective: ev.effectiveDirective || null, url: url });
      record({ type: 'CSP·' + directive, url: url, timeLocal: ts(now), timeISO: now.toISOString() });
      probeResult = 'online'; // CSP block proves we're online
      updateNetPanel('csp: ' + directive);
      setDiagnosis('csp', '🔒 Content-Security-Policy hat ein Asset blockiert (' + directive + ') — Server-Konfiguration, kein Client-Blocker.');
      reportDiagnosis('csp', 'CSP·' + directive, url, { directive: directive, effective: ev.effectiveDirective || null });
      show();
    });
    // Only trigger on REAL asset load failures. Failed/cancelled modulepreload
    // links are normal during route changes and do not mean that the page is
    // broken. Therefore links only count when they are actual stylesheets.
    window.addEventListener('error', function(e){
      var t = e && e.target;
      if (!t || t === window || !t.tagName) return;
      var tag = t.tagName;
      if (tag !== 'LINK' && tag !== 'SCRIPT') return;
      if (tag === 'LINK' && String(t.rel || t.getAttribute('rel') || '').toLowerCase() !== 'stylesheet') return;
      var url = urlOf(t);
      if (!isAppAsset(url)) return;
      var now = new Date();
      record({ type: typeOf(t), url: url, timeLocal: ts(now), timeISO: now.toISOString() });
      show();
      // Kick off (or re-use) a reachability probe so we can distinguish
      // network outage vs. content blocking.
      if (probeResult === null) runProbe(); else classify();
    }, true);
  })();
`;

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Outlet />
        <Toaster theme="dark" position="top-right" />
        <DiagnosticBanner />
        <DevErrorOverlay />
        <ClientErrorReporter />
      </AuthProvider>
    </QueryClientProvider>
  );
}
